import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import api from '../../../services/api';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import { formatDate } from '../../../utils/Utils';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';
import { useAuth } from '../../../context/AuthContext';
import { terminateEmployees, deleteEmployeeRecord } from '../../../services/employeeService';
import { formatPhone } from '../../../utils/phone';
import { toPriorServiceDays, priorServiceParts } from '../priorService';

// HR Analytics veri temeli: Employee.ExitReasonCode, OdExitTypeRules.ValidExitTypes /
// OdOffboardingDataAccess.IsValidExitType ile BİREBİR aynı sekiz kod. Değiştirme,
// ekleme, yeniden adlandırma yapma — iki modül aynı sözlüğü paylaşır.
const EXIT_REASON_CODES = [
  'voluntary_resignation', 'retirement', 'mutual_agreement', 'termination',
  'contract_end', 'health_reasons', 'military_service', 'woman_marriage_4857_14',
];

// contractEndDate/probationEndDate/terminationDate are optional DateTime? on the
// backend. GenericFormDrawer's DateTimePicker emits '' (not null) once a date field
// has been touched-then-cleared; System.Text.Json rejects "" for DateTime? and fails
// the whole request body (mirrors NULLABLE_FIELDS in pages/tenants/config/index.js).
const NULLABLE_DATE_FIELDS = ['contractEndDate', 'probationEndDate', 'terminationDate'];
const cleanDateFields = (formData) => {
  const cleaned = { ...formData };
  NULLABLE_DATE_FIELDS.forEach((k) => {
    if (cleaned[k] === '') cleaned[k] = null;
  });
  return cleaned;
};

// AKM POC §6.1/§6.4 — personel statüsü ve kıdeme eklenecek önceki hizmet.
const statusFields = (formData) => ({
  employeeStatusId: formData.employeeStatusId || null,
  priorServiceDays: toPriorServiceDays(formData.priorServiceDays),
});

// Süzgeç çipi kimlik değil AD göstersin diye seçicinin gördüğü adlar burada tutulur.
const employeeStatusNames = new Map();

const searchEmployeeStatuses = async (q) => {
  const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
  const res = await api.get('/v1/employee-statuses', { params });
  const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
  return items.map((s) => {
    employeeStatusNames.set(String(s.id), s.name);
    return { id: s.id, name: s.name };
  });
};

const getEmployeeStatusById = async (id) => {
  const res = await api.get(`/v1/employee-statuses/${id}`);
  const name = res.data?.data?.name || '';
  if (name) employeeStatusNames.set(String(id), name);
  return { id, name };
};

const useEmployeeApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { canWrite, hasModule } = useAuth();
  // Users without EM.Write (and not enterpriseAdmin/superAdmin, which canWrite
  // already bypasses to true) see a stripped list: only the 5 public columns
  // and matching filters. Everything personal is both hidden AND not sent from
  // the backend.
  const canSeeAll = canWrite ? canWrite('EM') : true;
  // OD (İşten Ayrılış) module active for the tenant → TerminationDate/ExitReasonCode
  // are read-only on the Employee record (OdOffboarding is the single source of
  // truth there). Uses the same tenant-module-activation source AuthContext.hasModule
  // reads elsewhere (not a raw privilege check — module activation is independent of
  // the current user's role, per the backend's IsOdActiveAsync/GetActiveModuleCodesAsync).
  const odActive = hasModule ? hasModule('OD') : false;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/employees?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      const payload = {
        ...cleanDateFields(formData),
        collarType: formData.collarType ? parseInt(formData.collarType) : null,
        employmentType: formData.employmentType ? parseInt(formData.employmentType) : null,
        ...statusFields(formData),
      };
      await api.post('/v1/employees', payload);
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      const payload = {
        ...cleanDateFields(formData),
        collarType: formData.collarType ? parseInt(formData.collarType) : null,
        employmentType: formData.employmentType ? parseInt(formData.employmentType) : null,
        ...statusFields(formData),
      };
      await api.put(`/v1/employees/${id}`, payload);
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  // İşten çıkarma. Tekil/toplu ayrımı KALKTI: tek uç, tek kapı kümesi (OD aktif mi,
  // devam eden ayrılış süreci var mı). Çıkış tarihi ve nedeni zorunlu — eski silme
  // yolu bu ikisini hiç yazmadığı için çıkan kişi İK Analitiği'nde ayrılan olarak
  // SAYILMIYORDU (kadro düşüyor, devir oranı yükselmiyordu).
  const terminate = useCallback(async ({ ids, terminationDate, exitReasonCode }) => {
    try {
      await terminateEmployees({ ids, terminationDate, exitReasonCode });
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Hatalı kayıt temizliği. Dönüş sözleşmesi çıkarmadakinden FARKLI: başarısızlıkta
  // sadece false değil, sunucunun bulduğu izler de döner ({ ok, traces }) — ekran
  // "silinemez" demekle yetinmeyip NE bulunduğunu sayarak gösterebilsin.
  const deleteRecord = useCallback(async (id) => {
    try {
      await deleteEmployeeRecord(id);
      setRefreshTrigger((prev) => prev + 1);
      return { ok: true };
    } catch (err) {
      const { status, data } = err?.response || {};
      if (status === 409 && data?.message === 'employee.hasHistory') {
        return { ok: false, traces: data?.data || {} };
      }
      // Başka her hata interceptor'ın işi (toast'ı zaten attı).
      return { ok: false };
    }
  }, []);

  // Re-hire a former (terminated) employee: flips IsActive back on with a new
  // employment date. Backend rejects if the person is BoomerangBlocked.
  const reactivate = useCallback(async (id, employmentDate, corporateEmail) => {
    try {
      // corporateEmail boşsa gönderilmez: backend eski adresi korur (ve başkasındaysa 409 döner).
      await api.post(`/v1/employees/${id}/reactivate`, { employmentDate, corporateEmail: corporateEmail || undefined });
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const collarOptions = useMemo(() => [
    { id: 1, name: t('employee.whiteCollar') },
    { id: 2, name: t('employee.blueCollar') },
  ], [t]);

  const employmentTypeOptions = useMemo(() => [
    { id: 1, name: t('employee.fullTime') },
    { id: 2, name: t('employee.partTime') },
    { id: 3, name: t('employee.contract') },
  ], [t]);

  const exitReasonOptions = useMemo(() => EXIT_REASON_CODES.map((c) => ({
    id: c, name: t(`employee.exitReasons.${c}`),
  })), [t]);

  const columns = useMemo(() => {
    const fullNameCol = {
      name: 'fullName',
      label: t('employee.fullName'),
      options: {
        filter: false, sort: true,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return <PersonCell name={`${row.firstName} ${row.lastName}`} profilePicture={row.profilePicture} />;
        },
      },
    };

    if (!canSeeAll) {
      // Restricted view: only the 5 public columns + hidden firstName/lastName
      // text filters to drive the fullName[like] lookup.
      return [
        { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
        fullNameCol,
        { name: 'firstName', label: t('employee.firstName'), options: { filter: true, sort: false, display: 'excluded', filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
        { name: 'lastName', label: t('employee.lastName'), options: { filter: true, sort: false, display: 'excluded', filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
        { name: 'corporateEmail', label: t('employee.corporateEmail'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
        { name: 'departmentName', label: t('employee.department'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
        { name: 'titleName', label: t('employee.titleName'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      ];
    }

    return [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    fullNameCol,
    { name: 'firstName', label: t('employee.firstName'), options: { filter: true, sort: false, display: 'excluded', filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'lastName', label: t('employee.lastName'), options: { filter: true, sort: false, display: 'excluded', filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'corporateEmail', label: t('employee.corporateEmail'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'personalEmail', label: t('employee.personalEmail'), options: { filter: true, sort: false, display: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'phoneNumber',
      label: t('employee.phone'),
      options: {
        filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return formatPhone(row?.areaCode, row?.phoneNumber);
        },
      },
    },
    { name: 'citizenId', label: t('employee.citizenId'), options: { filter: true, sort: false, display: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'registrationNumber', label: t('employee.registrationNumber'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'departmentName', label: t('employee.department'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    { name: 'titleName', label: t('employee.titleName'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    // Kademe / iş ailesi yalnız tam (EM.Write) kolon setinde — sunucu da yalnız EM.Write'a doldurur.
    { name: 'gradeName', label: t('jobGrade.grade'), options: { filter: false, sort: false, display: false, customBodyRender: (v) => v || '-' } },
    { name: 'jobFamilyName', label: t('jobFamily.jobFamily'), options: { filter: false, sort: false, display: false, customBodyRender: (v) => v || '-' } },
    { name: 'locationName', label: t('employee.branch'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    { name: 'managerName', label: t('employee.manager'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    {
      name: 'collarType', label: t('employee.collarType'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => v === 1 ? t('employee.whiteCollar') : v === 2 ? t('employee.blueCollar') : '-',
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('employee.collarType')}</InputLabel>
              <Select value={fl[i][0] || ''} onChange={(e) => { fl[i] = e.target.value ? [e.target.value] : []; onChange(fl[i], i, col); }}>
                <MenuItem value=""><em>-</em></MenuItem>
                {collarOptions.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'employmentType', label: t('employee.employmentType'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => v === 1 ? t('employee.fullTime') : v === 2 ? t('employee.partTime') : v === 3 ? t('employee.contract') : '-',
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('employee.employmentType')}</InputLabel>
              <Select value={fl[i][0] || ''} onChange={(e) => { fl[i] = e.target.value ? [e.target.value] : []; onChange(fl[i], i, col); }}>
                <MenuItem value=""><em>-</em></MenuItem>
                {employmentTypeOptions.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      // Personel statüsü: süzgeç anahtarı kolon adı DEĞİL, `employeeStatusId[eq]` (parseTableParams
      // düz değerde filterOptions.filterKey'i okur). Seçici kimlik yazar, tabloda ad görünür.
      name: 'employeeStatusName', label: t('employee.employeeStatus'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => v || '-',
        customFilterListOptions: { render: (v) => employeeStatusNames.get(String(v)) || v },
        filterOptions: {
          defaultOp: 'eq',
          filterKey: 'employeeStatusId[eq]',
          display: (fl, onChange, i, col) => (
            <AsyncGenericSelect
              label={t('employee.employeeStatus')}
              value={fl[i][0] || null}
              onChange={(val) => { fl[i] = val ? [val] : []; onChange(fl[i], i, col); }}
              onSearch={searchEmployeeStatuses}
              onGetById={getEmployeeStatusById}
            />
          ),
        },
      },
    },
    dateFilterColumn('dateOfBirth', t('employee.dateOfBirth'), lang),
    dateFilterColumn('employmentDate', t('employee.employmentDate'), lang),
    { name: 'address', label: t('employee.address'), options: { filter: true, sort: false, display: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    ];
  }, [t, data, lang, collarOptions, employmentTypeOptions, canSeeAll]);

  const objectSchema = useMemo(() => {
    // Local "today" (yyyy-MM-dd) — used as the maxDate bound for birth date so a
    // future date can't be picked/typed. Recomputed when the memo deps change.
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    // Birth date lower bound: 100 years ago (guards against typos like the year 1116).
    const minBirth = new Date(now.getFullYear() - 100, now.getMonth(), now.getDate());
    const minBirthStr = `${minBirth.getFullYear()}-${String(minBirth.getMonth() + 1).padStart(2, '0')}-${String(minBirth.getDate()).padStart(2, '0')}`;
    return [
    { name: 'profilePicture', label: t('employee.profilePicture'), type: 'pictureUpload', grid: 12, purpose: 'avatar' },
    { name: 'firstName', label: t('employee.firstName'), type: 'text', grid: 6, required: true },
    { name: 'lastName', label: t('employee.lastName'), type: 'text', grid: 6, required: true },
    // Zorunlu: iş postalarının alıcı adresi budur (backend'de NOT NULL). Boş bırakılabildiği
    // sürece, adresi olmayan çalışan sessizce hiç bildirim almıyordu.
    { name: 'corporateEmail', label: t('employee.corporateEmail'), type: 'email', grid: 6, required: true },
    { name: 'personalEmail', label: t('employee.personalEmail'), type: 'email', grid: 6 },
    { name: 'phoneNumber', label: t('employee.phone'), type: 'phone', grid: 12, dialCodeField: 'areaCode' },
    { name: 'citizenId', label: t('employee.citizenId'), type: 'text', grid: 6 },
    { name: 'registrationNumber', label: t('employee.registrationNumber'), type: 'text', grid: 6, description: t('employee.registrationNumberHint') },
    { name: 'dateOfBirth', label: t('employee.dateOfBirth'), type: 'date', grid: 6, required: true, minDate: minBirthStr, maxDate: todayStr },
    { name: 'employmentDate', label: t('employee.employmentDate'), type: 'date', grid: 6, required: true, minDate: '1900-01-01' },
    { name: 'contractEndDate', label: t('employee.contractEndDate'), type: 'date', grid: 6 },
    { name: 'probationEndDate', label: t('employee.probationEndDate'), type: 'date', grid: 6 },
    // OD (İşten Ayrılış) modülü aktifse bu iki alan salt okunurdur — OdOffboarding tek
    // doğru kaynak, backend değişmeyen (echo) değeri kabul eder, değişikliği reddeder
    // (ha.employee.exitFieldsManagedByOffboarding). Formdan tamamen kaldırıyoruz (hidden)
    // — GenericFormDrawer hidden alanların formData değerini submit'te olduğu gibi
    // gönderir, bu da "değişmeyen echo" davranışını doğal olarak sağlar.
    // Bu alan PLANLI (ileri tarihli) çıkış içindir; kaydı aktif listede bırakır ve
    // erişimi çıkış günü EmploymentTerminationWorker kapatır. Hemen çıkarmak listedeki
    // "İşten Çıkar" aksiyonunun işidir. İkisinin farkı ekranda yazmadığı için İK
    // buradan tarih girip kaydı "Çıkmış Personeller"de bekliyordu (analiz bulgusu B).
    {
      name: 'terminationDate', label: t('employee.terminationDate'), type: 'date', grid: 6, hidden: odActive,
      description: t('employee.terminationDateHint'),
    },
    {
      name: 'exitReasonCode', label: t('employee.exitReasonCode'), type: 'select', grid: 6,
      options: exitReasonOptions, idKey: 'id', labelKey: 'name', hidden: odActive,
    },
    { name: 'collarType', label: t('employee.collarType'), type: 'select', grid: 6, options: collarOptions, idKey: 'id', labelKey: 'name' },
    { name: 'employmentType', label: t('employee.employmentType'), type: 'select', grid: 6, options: employmentTypeOptions, idKey: 'id', labelKey: 'name' },
    {
      name: 'employeeStatusId', label: t('employee.employeeStatus'), type: 'asyncSelect', grid: 6,
      onSearch: searchEmployeeStatuses,
      onGetById: getEmployeeStatusById,
    },
    {
      name: 'priorServiceDays', label: t('employee.priorServiceDays'), type: 'number', grid: 6, min: 0, max: 36500,
      // İpucu alanın altında (GenericFormDrawer Description: pozitif üst marj).
      description: (formData) => {
        const parts = priorServiceParts(formData?.priorServiceDays);
        return parts ? t('employee.priorServiceHint', parts) : '';
      },
    },
    { name: 'address', label: t('employee.address'), type: 'textarea', grid: 12 },
    { name: 'tz', label: t('employee.timezone'), type: 'timezone', grid: 6 },
    {
      name: 'locationId', label: t('employee.location'), type: 'asyncSelect', grid: 6,
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/locations', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map(l => ({ id: l.id, name: l.name }));
      },
      onGetById: async (id) => {
        const res = await api.get('/v1/locations', { params: { pageSize: 100 } });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        const loc = items.find(l => l.id === id);
        return loc ? { id: loc.id, name: loc.name } : { id, name: '' };
      },
    },
    {
      name: 'holidayCalendarId', label: t('employee.holidayCalendar'), type: 'asyncSelect', grid: 6,
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/holiday-calendars', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map(c => ({ id: c.id, name: c.name }));
      },
      onGetById: async (id) => {
        const res = await api.get('/v1/holiday-calendars', { params: { pageSize: 100 } });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        const cal = items.find(c => c.id === id);
        return cal ? { id: cal.id, name: cal.name } : { id, name: '' };
      },
    },
    {
      name: 'subTenantId', label: t('employee.subTenant'), type: 'asyncSelect', grid: 6,
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/sub-tenants', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map(t => ({ id: t.id, name: t.name }));
      },
      onGetById: async (id) => {
        const res = await api.get('/v1/sub-tenants', { params: { pageSize: 100 } });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        const st = items.find(s => s.id === id);
        return st ? { id: st.id, name: st.name } : { id, name: '' };
      },
    },
    // Organizasyondaki yeri — işe alım modülü olmayan müşteriler kişiyi buradan yerleştirir. Seçim arkada otomatik kodlu
    // bir koltuk açar (Excel/Public API kuralı, backend EmployeeFormPlacement); yönetici, yöneticinin koltuğunu üst pozisyon
    // yapar. Düzenlemede boş alan "değiştirme" demektir — kişiyi koltuktan almak organizasyon şemasının işidir.
    {
      name: 'departmentId', label: t('employee.department'), type: 'asyncSelect', grid: 6,
      description: t('employee.orgPlacementHint'),
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/departments', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map(d => ({ id: d.id, name: d.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/departments/${id}`);
        const d = res.data?.data;
        return d ? { id: d.id, name: d.name } : { id, name: '' };
      },
    },
    {
      name: 'titleId', label: t('employee.titleName'), type: 'asyncSelect', grid: 6,
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/titles', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map(ti => ({ id: ti.id, name: ti.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/titles/${id}`);
        const ti = res.data?.data;
        return ti ? { id: ti.id, name: ti.name } : { id, name: '' };
      },
    },
    {
      name: 'managerId', label: t('employee.manager'), type: 'asyncSelect', grid: 12,
      description: t('employee.managerHint'),
      onSearch: async (q) => {
        const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map(e => ({
          id: e.id,
          name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
          profilePicture: e.profilePicture ?? null,
        }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/employees/${id}`);
        const e = res.data?.data;
        return e
          ? { id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture ?? null }
          : { id, name: '' };
      },
    },
    ];
  }, [t, collarOptions, employmentTypeOptions, exitReasonOptions, odActive]);

  return { objectSchema, columns, data, fetching, pagination, create, update, terminate, deleteRecord, reactivate, odActive, exitReasonOptions };
};

export default useEmployeeApi;
